import { useState, useEffect, useRef } from "react";
import { API_ENDPOINTS, API_URL } from "@/constants/constants";
import {
  RefreshCw,
  Search,
  PlusCircle,
  X,
  Eye,
  EyeOff,
  Edit,
  ToggleLeft,
  ToggleRight,
  Upload,
  User,
} from "lucide-react";

// Interface pour les utilisateurs
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles?: string[];
  activeRole: string;
  createdAt: string;
  isVerified: boolean;
  isActive?: boolean;
  fonction?: string;
  service?: string;
  sponsorId?: string;
  profileImage?: string;
  citation?: string;
  ministry?: Ministry;
}

// Interface pour les superviseurs
interface Supervisor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

// Interface pour les ministères
interface Ministry {
  id: number;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

// Interface pour le formulaire de création d'utilisateur
interface CreateUserForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  roles: string[];
  activeRole: string;
  fonction: string;
  ministryId: string;
  birthdate: string;
  sponsorId?: string; // ID du parrain (optionnel)
  image: string; // Nom du champ envoyé à l'API
  citation: string;
}

// Interface pour le formulaire de mise à jour d'utilisateur
interface UpdateUserForm {
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  activeRole: string;
  fonction: string;
  ministryId: string;
  sponsorId?: string;
  image: string; // Nom du champ envoyé à l'API
  citation: string;
}

const AdminUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [loadingMinistries, setLoadingMinistries] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    roles: [],
    activeRole: "APPRENANT",
    fonction: "",
    ministryId: "",
    birthdate: "",
    sponsorId: "",
    image: "",
    citation: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<string | null>(
    null
  );
  const [updateForm, setUpdateForm] = useState<UpdateUserForm>({
    firstName: "",
    lastName: "",
    email: "",
    roles: [],
    activeRole: "",
    fonction: "",
    ministryId: "",
    sponsorId: "",
    image: "",
    citation: "",
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const fileInputCreateRef = useRef<HTMLInputElement>(null);
  const fileInputUpdateRef = useRef<HTMLInputElement>(null);
  const fileInputImportRef = useRef<HTMLInputElement>(null);
  const [createSelectedFile, setCreateSelectedFile] = useState<File | null>(
    null
  );
  const [updateSelectedFile, setUpdateSelectedFile] = useState<File | null>(
    null
  );
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importSelectedFile, setImportSelectedFile] = useState<File | null>(
    null
  );
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState(false);
  const [createTempImagePreview, setCreateTempImagePreview] = useState<
    string | null
  >(null);
  const [updateTempImagePreview, setUpdateTempImagePreview] = useState<
    string | null
  >(null);

  useEffect(() => {
    fetchUsers();
    fetchSupervisors();
    fetchMinistries();
  }, []);

  const fetchSupervisors = async () => {
    setLoadingSupervisors(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        return;
      }

      // Endpoint pour récupérer la liste des superviseurs
      const apiUrl = `${API_URL}/api/v1/users/supervisors/all`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        console.error("Erreur lors de la récupération des superviseurs:", data);
        return;
      }

      setSupervisors(data);
    } catch (err) {
      console.error("Erreur lors de la récupération des superviseurs:", err);
    } finally {
      setLoadingSupervisors(false);
    }
  };

  const fetchMinistries = async () => {
    setLoadingMinistries(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        return;
      }

      // Endpoint pour récupérer la liste des ministères
      const apiUrl = `${API_URL}/api/v1/ministries`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        console.error("Erreur lors de la récupération des ministères:", data);
        return;
      }

      // Gérer la structure de réponse { success: true, data: [...] } ou directement []
      const ministriesList = data.success && data.data ? data.data : data;
      setMinistries(ministriesList);
    } catch (err) {
      console.error("Erreur lors de la récupération des ministères:", err);
    } finally {
      setLoadingMinistries(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(API_ENDPOINTS.AUTH.GET_USERS, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la récupération des utilisateurs"
        );
      }

      // Gérer la structure de réponse { success: true, data: [...] } ou directement []
      const usersList = data.success && data.data ? data.data : data;
      setUsers(usersList);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de récupération des utilisateurs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError("");
    setCreateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Préparation des données avec FormData pour pouvoir inclure le fichier
      const formData = new FormData();

      // Ajouter toutes les données du formulaire
      formData.append("firstName", createForm.firstName);
      formData.append("lastName", createForm.lastName);
      formData.append("email", createForm.email);
      formData.append("password", createForm.password);
      // Ajouter chaque rôle individuellement au lieu de JSON.stringify
      createForm.roles.forEach((role) => {
        formData.append("roles", role);
      });
      formData.append("activeRole", createForm.activeRole);
      formData.append("fonction", createForm.fonction);
      if (createForm.ministryId) {
        formData.append("ministry_id",  (createForm.ministryId));
      }

      // Convertir birthdate en objet Date et l'ajouter au format ISO string
      const birthdateObj = new Date(createForm.birthdate);
      formData.append("birthdate", birthdateObj.toISOString());

      // Ajouter le sponsorId uniquement s'il existe et le convertir en nombre
      if (createForm.sponsorId) {
        formData.append("sponsorId", Number.parseInt(createForm.sponsorId).toString());
      }

      // Ajouter la citation si elle existe
      if (createForm.citation) {
        formData.append("citation", createForm.citation);
      }

      // Ajouter le fichier image s'il est sélectionné
      if (createSelectedFile) {
        formData.append("profileImage", createSelectedFile);
        console.log(
          "Image ajoutée au formulaire (champ profileImage):",
          createSelectedFile.name
        );
      }

      console.log("Envoi des données utilisateur avec FormData");

      const response = await fetch(API_ENDPOINTS.AUTH.REGISTER, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          // Ne pas définir Content-Type, il sera automatiquement défini avec boundary pour FormData
        },
        body: formData,
      });

      const data = await response.json();
      console.log("Réponse API après création:", data);

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la création de l'utilisateur"
        );
      }

      // Rafraîchir la liste des utilisateurs
      fetchUsers();

      // Réinitialiser le formulaire et afficher un message de succès
      setCreateForm({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        roles: [],
        activeRole: "APPRENANT",
        fonction: "",
        ministryId: "",
        birthdate: "",
        sponsorId: "",
        image: "",
        citation: "",
      });

      // Nettoyer l'aperçu de l'image
      if (createTempImagePreview) {
        URL.revokeObjectURL(createTempImagePreview);
        setCreateTempImagePreview(null);
      }
      setCreateSelectedFile(null);
      if (fileInputCreateRef.current) {
        fileInputCreateRef.current.value = "";
      }

      setCreateSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsCreateModalOpen(false);
        setCreateSuccess(false);
      }, 2000);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de création d'utilisateur:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Gérer la sélection des rôles multiples pour la création
  const handleCreateRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setCreateForm((prev) => ({
      ...prev,
      roles: checked
        ? [...prev.roles, value]
        : prev.roles.filter((role) => role !== value),
    }));
  };

  // Gérer la sélection des rôles multiples pour la mise à jour
  const handleUpdateRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setUpdateForm((prev) => ({
      ...prev,
      roles: checked
        ? [...prev.roles, value]
        : prev.roles.filter((role) => role !== value),
    }));
  };

  const handleToggleUserStatus = async (
    userId: string,
    currentStatus: boolean
  ) => {
    setStatusUpdateLoading(userId);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const apiUrl = API_ENDPOINTS.AUTH.LOGIN.replace(
        "/login",
        `/users/${userId}/status`
      );

      const response = await fetch(apiUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ isActive: !currentStatus }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.message || "Erreur lors de la mise à jour du statut"
        );
      }

      // Rafraîchir la liste des utilisateurs après la mise à jour
      fetchUsers();
    } catch (err) {
      console.error("Erreur lors de la modification du statut:", err);
      alert(err instanceof Error ? err.message : "Une erreur est survenue");
    } finally {
      setStatusUpdateLoading(null);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUpdateForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roles: user.roles || [],
      activeRole: user.activeRole,
      fonction: user.fonction || "",
      ministryId: user.ministry?.id ? user.ministry.id.toString() : "",
      sponsorId: user.sponsorId || "",
      image: user.profileImage || "",
      citation: user.citation || "",
    });
    setIsUpdateModalOpen(true);
  };

  const handleUpdateInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setUpdateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setUpdateLoading(true);
    setUpdateError("");
    setUpdateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Préparation des données avec FormData pour pouvoir inclure le fichier
      const formData = new FormData();

      // Ajouter toutes les données du formulaire
      formData.append("firstName", updateForm.firstName);
      formData.append("lastName", updateForm.lastName);
      formData.append("email", updateForm.email);
      // Ajouter chaque rôle individuellement au lieu de JSON.stringify
      updateForm.roles.forEach((role) => {
        formData.append("roles", role);
      });
      formData.append("activeRole", updateForm.activeRole);
      formData.append("fonction", updateForm.fonction);
      if (updateForm.ministryId) {
        formData.append("ministry_id", (updateForm.ministryId));
      }

      // Ajouter le sponsorId uniquement s'il existe et le convertir en nombre
      if (updateForm.sponsorId) {
        formData.append("sponsorId", Number.parseInt(updateForm.sponsorId).toString());
      }

      // Ajouter la citation si elle existe
      if (updateForm.citation) {
        formData.append("citation", updateForm.citation);
      }

      // Ajouter le fichier image s'il est sélectionné
      if (updateSelectedFile) {
        formData.append("profileImage", updateSelectedFile);
        console.log(
          "Image ajoutée au formulaire de mise à jour (champ profileImage):",
          updateSelectedFile.name
        );
      }

      // Endpoint pour mettre à jour un utilisateur
      const apiUrl = API_ENDPOINTS.AUTH.LOGIN.replace(
        "/login",
        `/users/${selectedUser.id}`
      );

      console.log("Mise à jour utilisateur URL:", apiUrl);
      console.log(
        "Envoi des données utilisateur avec FormData pour mise à jour"
      );

      const response = await fetch(apiUrl, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          // Ne pas définir Content-Type, il sera automatiquement défini avec boundary pour FormData
        },
        body: formData,
      });

      // Si la réponse n'est pas ok, essayer de lire le corps de la réponse pour le message d'erreur
      if (!response.ok) {
        let errorMessage = "Erreur lors de la mise à jour de l'utilisateur";
        try {
          const errorData = await response.json();
          console.error("Erreur API (détails):", errorData);
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Si la réponse ne contient pas de JSON valide, utiliser le statut HTTP
          errorMessage = `Erreur ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Récupérer la réponse si nécessaire
      try {
        const data = await response.json();
        console.log("Réponse API après mise à jour:", data);
      } catch {
        console.log("Pas de corps JSON dans la réponse de mise à jour");
        // Ignorer les erreurs de parsing JSON si le corps est vide
      }

      // Rafraîchir la liste des utilisateurs
      fetchUsers();

      // Nettoyer l'aperçu de l'image
      if (updateTempImagePreview) {
        URL.revokeObjectURL(updateTempImagePreview);
        setUpdateTempImagePreview(null);
      }
      setUpdateSelectedFile(null);
      if (fileInputUpdateRef.current) {
        fileInputUpdateRef.current.value = "";
      }

      setUpdateSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsUpdateModalOpen(false);
        setUpdateSuccess(false);
        setSelectedUser(null);
      }, 2000);
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur lors de la mise à jour de l'utilisateur:", err);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Gérer les changements dans les fichiers
  const handleCreateFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Nettoyer l'URL précédente si elle existe
      if (createTempImagePreview) {
        URL.revokeObjectURL(createTempImagePreview);
      }

      // Créer une URL temporaire pour l'aperçu
      const imageUrl = URL.createObjectURL(file);
      setCreateTempImagePreview(imageUrl);
      setCreateSelectedFile(file);
      console.log(
        "Fichier sélectionné pour création:",
        file.name,
        file.type,
        Math.round(file.size / 1024) + "KB"
      );
    }
  };

  const handleUpdateFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Nettoyer l'URL précédente si elle existe
      if (updateTempImagePreview) {
        URL.revokeObjectURL(updateTempImagePreview);
      }

      // Créer une URL temporaire pour l'aperçu
      const imageUrl = URL.createObjectURL(file);
      setUpdateTempImagePreview(imageUrl);
      setUpdateSelectedFile(file);
      console.log(
        "Fichier sélectionné pour mise à jour:",
        file.name,
        file.type,
        Math.round(file.size / 1024) + "KB"
      );
    }
  };

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportSelectedFile(file);
      console.log(
        "Fichier Excel sélectionné pour import:",
        file.name,
        file.type,
        Math.round(file.size / 1024) + "KB"
      );
    }
  };

  const handleImportUsers = async (e: React.FormEvent) => {
    e.preventDefault();
    setImportLoading(true);
    setImportError("");
    setImportSuccess(false);

    try {
      if (!importSelectedFile) {
        throw new Error("Veuillez sélectionner un fichier Excel");
      }

      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Préparation des données avec FormData
      const formData = new FormData();
      formData.append("file", importSelectedFile);

      console.log("Envoi du fichier Excel pour import:", importSelectedFile.name);

      const response = await fetch(API_ENDPOINTS.AUTH.IMPORT_USERS, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      console.log("Réponse API après import:", data);

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de l'import des utilisateurs"
        );
      }

      // Rafraîchir la liste des utilisateurs
      fetchUsers();

      setImportSuccess(true);
      setImportSelectedFile(null);
      if (fileInputImportRef.current) {
        fileInputImportRef.current.value = "";
      }

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsImportModalOpen(false);
        setImportSuccess(false);
      }, 2000);
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur lors de l'import des utilisateurs:", err);
    } finally {
      setImportLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.activeRole.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Gestion Utilisateurs</h1>
      <p className="mt-4">Gérez les utilisateurs de la plateforme.</p>

      <div className="flex justify-between items-center mt-8">
        <div className="relative w-64">
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            className="w-full p-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={16}
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            <PlusCircle size={16} />
            Créer un utilisateur
          </button>

          <button
            onClick={() => setIsImportModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
          >
            <Upload size={16} />
            Importer Excel
          </button>

          <button
            onClick={fetchUsers}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Liste des utilisateurs</h2>

        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && filteredUsers.length === 0 && (
          <p>Aucun utilisateur trouvé.</p>
        )}

        {!loading && !error && filteredUsers.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    N°
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Nom
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Email
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Rôle
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Ministère
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Vérifié
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Statut
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Date d'inscription
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 border-b text-center">
                      {index + 1}
                    </td>
                    <td className="py-3 px-4 border-b">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="py-3 px-4 border-b">{user.email}</td>
                    <td className="py-3 px-4 border-b">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          user.activeRole === "ADMIN"
                            ? "bg-purple-100 text-purple-800"
                            : user.activeRole === "FORMATEUR"
                            ? "bg-blue-100 text-blue-800"
                            : user.activeRole === "APPRENANT"
                            ? "bg-green-100 text-green-800"
                            : user.activeRole === "SUPERVISEUR"
                            ? "bg-orange-100 text-orange-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {user.activeRole}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b">
                      {user.ministry ? user.ministry.name : "--"}
                    </td>
                    <td className="py-3 px-4 border-b">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          user.isVerified
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.isVerified ? "Oui" : "Non"}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          user.isActive !== false
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.isActive !== false ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b">
                      {new Date(user.createdAt).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="py-3 px-4 border-b">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                          title="Modifier"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() =>
                            handleToggleUserStatus(
                              user.id,
                              Boolean(user.isActive)
                            )
                          }
                          className={`p-1 rounded ${
                            user.isActive
                              ? "text-green-600 hover:text-green-800 hover:bg-green-100"
                              : "text-red-600 hover:text-red-800 hover:bg-red-100"
                          }`}
                          title={user.isActive ? "Désactiver" : "Activer"}
                          disabled={statusUpdateLoading === user.id}
                        >
                          {statusUpdateLoading === user.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                          ) : user.isActive ? (
                            <ToggleRight size={18} />
                          ) : (
                            <ToggleLeft size={18} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de création d'utilisateur */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-5xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Créer un utilisateur</h2>

            {createSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Utilisateur créé avec succès!</p>
              </div>
            )}

            {createError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{createError}</p>
              </div>
            )}

            <form onSubmit={handleCreateUser}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Première colonne */}
                <div>
                  <div className="mb-4">
                    <label htmlFor="create-firstName" className="block text-gray-700 text-sm font-bold mb-2">
                      Prénom
                    </label>
                    <input
                      id="create-firstName"
                      type="text"
                      name="firstName"
                      value={createForm.firstName}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-email" className="block text-gray-700 text-sm font-bold mb-2">
                      Email
                    </label>
                    <input
                      id="create-email"
                      type="email"
                      name="email"
                      value={createForm.email}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-ministryId" className="block text-gray-700 text-sm font-bold mb-2">
                      Ministère
                    </label>
                    <select
                      id="create-ministryId"
                      name="ministryId"
                      value={createForm.ministryId}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Sélectionner un ministère --</option>
                      {loadingMinistries ? (
                        <option disabled>Chargement des ministères...</option>
                      ) : (
                        ministries.map((ministry) => (
                          <option key={ministry.id} value={ministry.id}>
                            {ministry.name}
                          </option>
                        ))
                      )}
                    </select>
                    {!ministries.length && !loadingMinistries && (
                      <p className="text-sm text-red-500 mt-1">
                        Aucun ministère disponible.
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-password" className="block text-gray-700 text-sm font-bold mb-2">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <input
                        id="create-password"
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={createForm.password}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff
                            size={18}
                            aria-label="Masquer le mot de passe"
                          />
                        ) : (
                          <Eye
                            size={18}
                            aria-label="Afficher le mot de passe"
                          />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Rôles (sélectionnez plusieurs rôles)
                      <div className="space-y-2 p-3 border rounded-md">
                        {["APPRENANT", "FORMATEUR", "SUPERVISEUR", "DRH", "ADMIN"].map(
                          (role) => (
                            <div key={role} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`create-role-${role}`}
                                value={role}
                                checked={createForm.roles.includes(role)}
                                onChange={handleCreateRoleChange}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            <label
                              htmlFor={`create-role-${role}`}
                              className="ml-2 text-sm text-gray-700 cursor-pointer"
                            >
                              {role === "APPRENANT"
                                ? "Apprenant"
                                : role === "FORMATEUR"
                                ? "Formateur"
                                : role === "SUPERVISEUR"
                                ? "Superviseur"
                                : role === "DRH"
                                ? "DRH"
                                : "Administrateur"}
                            </label>
                          </div>
                        )
                      )}
                      </div>
                    </label>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-activeRole" className="block text-gray-700 text-sm font-bold mb-2">
                      Rôle actif
                    </label>
                    <select
                      id="create-activeRole"
                      name="activeRole"
                      value={createForm.activeRole}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Sélectionner le rôle actif --</option>
                      {createForm.roles.length > 0 ? (
                        createForm.roles.map((role) => (
                          <option key={role} value={role}>
                            {role === "APPRENANT"
                              ? "Apprenant"
                              : role === "FORMATEUR"
                              ? "Formateur"
                              : role === "SUPERVISEUR"
                              ? "Superviseur"
                              : role === "DRH"
                              ? "DRH"
                              : "Administrateur"}
                          </option>
                        ))
                      ) : (
                        <option disabled>-- Sélectionnez d'abord des rôles --</option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Deuxième colonne */}
                <div>
                  <div className="mb-4">
                    <label htmlFor="create-lastName" className="block text-gray-700 text-sm font-bold mb-2">
                      Nom
                    </label>
                    <input
                      id="create-lastName"
                      type="text"
                      name="lastName"
                      value={createForm.lastName}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-fonction" className="block text-gray-700 text-sm font-bold mb-2">
                      Fonction
                    </label>
                    <input
                      id="create-fonction"
                      type="text"
                      name="fonction"
                      value={createForm.fonction}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Developer"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-birthdate" className="block text-gray-700 text-sm font-bold mb-2">
                      Date de naissance
                    </label>
                    <input
                      id="create-birthdate"
                      type="date"
                      name="birthdate"
                      value={createForm.birthdate}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-sponsorId" className="block text-gray-700 text-sm font-bold mb-2">
                      Parrain (optionnel)
                    </label>
                    <select
                      id="create-sponsorId"
                      name="sponsorId"
                      value={createForm.sponsorId}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Sélectionner un parrain --</option>
                      {loadingSupervisors ? (
                        <option disabled>Chargement des parrains...</option>
                      ) : (
                        supervisors.map((supervisor) => (
                          <option key={supervisor.id} value={supervisor.id}>
                            {supervisor.firstName} {supervisor.lastName} (
                            {supervisor.email})
                          </option>
                        ))
                      )}
                    </select>
                    {!supervisors.length && !loadingSupervisors && (
                      <p className="text-sm text-red-500 mt-1">
                        Aucun parrain disponible. Créez d'abord un utilisateur
                        avec le rôle SUPERVISEUR.
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-profileImage" className="block text-gray-700 text-sm font-bold mb-2">
                      Image de profil
                    </label>
                    <div className="flex items-center gap-4">
                      {/* Aperçu de l'image (thumbnail carré) */}
                      <div className="relative w-24 h-24 border border-gray-300 rounded-md overflow-hidden flex items-center justify-center bg-gray-100">
                        {createTempImagePreview ? (
                          <img
                            src={createTempImagePreview}
                            alt="Image de profil"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "";
                              (e.target as HTMLImageElement).alt =
                                "Image non disponible";
                            }}
                          />
                        ) : (
                          <User size={36} className="text-gray-400" />
                        )}
                      </div>

                      {/* Bouton upload */}
                      <div>
                        <input
                          id="create-profileImage"
                          type="file"
                          ref={fileInputCreateRef}
                          className="hidden"
                          accept="image/*"
                          onChange={handleCreateFileChange}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputCreateRef.current?.click()}
                          className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-md"
                        >
                          <Upload size={16} />
                          <span>Upload</span>
                        </button>
                      </div>

                      {/* Champ caché pour l'URL de l'image */}
                      <input
                        type="hidden"
                        name="image"
                        value={createForm.image || ""}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-citation" className="block text-gray-700 text-sm font-bold mb-2">
                      Citation / Description
                    </label>
                    <textarea
                      id="create-citation"
                      name="citation"
                      value={createForm.citation}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                      placeholder="Description ou citation personnelle"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6 sticky bottom-0 bg-white py-3 border-t">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
                  disabled={createLoading}
                >
                  {createLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'import des utilisateurs via Excel */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
            <button
              onClick={() => setIsImportModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Importer des utilisateurs</h2>

            {importSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Utilisateurs importés avec succès!</p>
              </div>
            )}

            {importError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{importError}</p>
              </div>
            )}

            <form onSubmit={handleImportUsers}>
              <div className="mb-6">
                <label htmlFor="import-file" className="block text-gray-700 text-sm font-bold mb-4">
                  Sélectionnez un fichier Excel (.xlsx, .xls)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
                  <input
                    id="import-file"
                    type="file"
                    ref={fileInputImportRef}
                    className="hidden"
                    accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleImportFileChange}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputImportRef.current?.click()}
                    className="flex items-center justify-center gap-2 mx-auto"
                  >
                    <Upload size={32} className="text-purple-500" />
                  </button>
                  {importSelectedFile ? (
                    <div>
                      <p className="text-gray-700 font-semibold mt-2">
                        {importSelectedFile.name}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {Math.round(importSelectedFile.size / 1024)} KB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-600 mt-2">Glissez-déposez un fichier</p>
                      <p className="text-sm text-gray-500">ou cliquez pour parcourir</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Le fichier Excel doit contenir les colonnes: firstName, lastName, email, roles, 
                  activeRole, fonction, birthdate, ministry_id
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsImportModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors flex items-center gap-2"
                  disabled={importLoading || !importSelectedFile}
                >
                  {importLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Importer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de modification d'utilisateur */}
      {isUpdateModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-5xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsUpdateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Modifier l'utilisateur</h2>

            {updateSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Utilisateur modifié avec succès!</p>
              </div>
            )}

            {updateError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{updateError}</p>
              </div>
            )}

            <form onSubmit={handleUpdateUser}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Première colonne */}
                <div>
                  <div className="mb-4">
                    <label htmlFor="update-firstName" className="block text-gray-700 text-sm font-bold mb-2">
                      Prénom
                    </label>
                    <input
                      id="update-firstName"
                      type="text"
                      name="firstName"
                      value={updateForm.firstName}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-email" className="block text-gray-700 text-sm font-bold mb-2">
                      Email
                    </label>
                    <input
                      id="update-email"
                      type="email"
                      name="email"
                      value={updateForm.email}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Rôles (sélectionnez plusieurs rôles)
                      <div className="space-y-2 p-3 border rounded-md">
                        {["APPRENANT", "FORMATEUR", "SUPERVISEUR", "DRH", "ADMIN"].map(
                          (role) => (
                            <div key={role} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`update-role-${role}`}
                                value={role}
                                checked={updateForm.roles.includes(role)}
                                onChange={handleUpdateRoleChange}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            <label
                              htmlFor={`update-role-${role}`}
                              className="ml-2 text-sm text-gray-700 cursor-pointer"
                            >
                              {role === "APPRENANT"
                                ? "Apprenant"
                                : role === "FORMATEUR"
                                ? "Formateur"
                                : role === "SUPERVISEUR"
                                ? "Superviseur"
                                : role === "DRH"
                                ? "DRH"
                                : "Administrateur"}
                            </label>
                          </div>
                        )
                      )}
                      </div>
                    </label>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-activeRole" className="block text-gray-700 text-sm font-bold mb-2">
                      Rôle actif
                    </label>
                    <select
                      id="update-activeRole"
                      name="activeRole"
                      value={updateForm.activeRole}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Sélectionner le rôle actif --</option>
                      {updateForm.roles.length > 0 ? (
                        updateForm.roles.map((role) => (
                          <option key={role} value={role}>
                            {role === "APPRENANT"
                              ? "Apprenant"
                              : role === "FORMATEUR"
                              ? "Formateur"
                              : role === "SUPERVISEUR"
                              ? "Superviseur"
                              : role === "DRH"
                              ? "DRH"
                              : "Administrateur"}
                          </option>
                        ))
                      ) : (
                        <option disabled>-- Sélectionnez d'abord des rôles --</option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Deuxième colonne */}
                <div>
                  <div className="mb-4">
                    <label htmlFor="update-lastName" className="block text-gray-700 text-sm font-bold mb-2">
                      Nom
                    </label>
                    <input
                      id="update-lastName"
                      type="text"
                      name="lastName"
                      value={updateForm.lastName}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-fonction" className="block text-gray-700 text-sm font-bold mb-2">
                      Fonction
                    </label>
                    <input
                      id="update-fonction"
                      type="text"
                      name="fonction"
                      value={updateForm.fonction}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Developer"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-ministryId" className="block text-gray-700 text-sm font-bold mb-2">
                      Ministère
                    </label>
                    <select
                      id="update-ministryId"
                      name="ministryId"
                      value={updateForm.ministryId}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Sélectionner un ministère --</option>
                      {loadingMinistries ? (
                        <option disabled>Chargement des ministères...</option>
                      ) : (
                        ministries.map((ministry) => (
                          <option key={ministry.id} value={ministry.id}>
                            {ministry.name}
                          </option>
                        ))
                      )}
                    </select>
                    {!ministries.length && !loadingMinistries && (
                      <p className="text-sm text-red-500 mt-1">
                        Aucun ministère disponible.
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-sponsorId" className="block text-gray-700 text-sm font-bold mb-2">
                      Parrain (optionnel)
                    </label>
                    <select
                      id="update-sponsorId"
                      name="sponsorId"
                      value={updateForm.sponsorId}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Sélectionner un parrain --</option>
                      {loadingSupervisors ? (
                        <option disabled>Chargement des parrains...</option>
                      ) : (
                        supervisors.map((supervisor) => (
                          <option key={supervisor.id} value={supervisor.id}>
                            {supervisor.firstName} {supervisor.lastName} (
                            {supervisor.email})
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-profileImage" className="block text-gray-700 text-sm font-bold mb-2">
                      Image de profil
                    </label>
                    <div className="flex items-center gap-4">
                      {/* Aperçu de l'image (thumbnail carré) */}
                      <div className="relative w-24 h-24 border border-gray-300 rounded-md overflow-hidden flex items-center justify-center bg-gray-100">
                        {updateTempImagePreview ? (
                          <img
                            src={updateTempImagePreview}
                            alt="Image de profil"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "";
                              (e.target as HTMLImageElement).alt =
                                "Image non disponible";
                            }}
                          />
                        ) : selectedUser && selectedUser.profileImage ? (
                          <img
                            src={`${API_URL}/${selectedUser.profileImage}`}
                            alt="Image de profil"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "";
                              (e.target as HTMLImageElement).alt =
                                "Image non disponible";
                            }}
                          />
                        ) : (
                          <User size={36} className="text-gray-400" />
                        )}
                      </div>

                      {/* Bouton upload */}
                      <div>
                        <input
                          id="update-profileImage"
                          type="file"
                          ref={fileInputUpdateRef}
                          className="hidden"
                          accept="image/*"
                          onChange={handleUpdateFileChange}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputUpdateRef.current?.click()}
                          className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-md"
                        >
                          <Upload size={16} />
                          <span>Upload</span>
                        </button>
                      </div>

                      {/* Champ caché pour l'URL de l'image */}
                      <input
                        type="hidden"
                        name="image"
                        value={updateForm.image || ""}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-citation" className="block text-gray-700 text-sm font-bold mb-2">
                      Citation / Description
                    </label>
                    <textarea
                      id="update-citation"
                      name="citation"
                      value={updateForm.citation}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                      placeholder="Description ou citation personnelle"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6 sticky bottom-0 bg-white py-3 border-t">
                <button
                  type="button"
                  onClick={() => setIsUpdateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={updateLoading}
                >
                  {updateLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
